import { z } from 'zod';
import { api } from '@shared/api';

const count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const quantity = z.number().int().min(Number.MIN_SAFE_INTEGER).max(Number.MAX_SAFE_INTEGER);
const timestamp = z.iso.datetime({ offset: true });
export const executionStatusSchema = z.enum([
  'pending',
  'running',
  'completed',
  'stopped',
  'reconciliation_required',
]);
export const executionOutcomeSchema = z.enum([
  'all',
  'updated',
  'unchanged',
  'conflict',
  'rejected',
  'unconfirmed',
  'other',
]);
const categoryCoverageSchema = z.object({
  recordedOutcomeCount: count.nullable(),
  attributedCount: count,
  unattributedOutcomeCount: count.nullable(),
});
const evidenceCoverageSchema = z.object({
  updated: categoryCoverageSchema,
  unchanged: categoryCoverageSchema,
  conflict: categoryCoverageSchema,
  rejected: categoryCoverageSchema,
  unconfirmed: categoryCoverageSchema,
  other: categoryCoverageSchema,
  updatedArticles: categoryCoverageSchema,
  admissionExclusions: z.literal('not_recorded_per_target'),
});
const countersSchema = z.object({
  updatedCards: count,
  updatedArticles: count,
  unchangedCards: count,
  conflictCards: count,
  rejectedCards: count,
  unconfirmedCards: count,
  otherCards: count,
});
export const executionSchema = z.object({
  executionId: z.uuid(),
  requestId: z.uuid().nullable(),
  trigger: z.enum(['manual', 'scheduled']).nullable(),
  status: executionStatusSchema,
  createdAt: timestamp,
  completedAt: timestamp.nullable(),
  locationId: z.string().min(1),
  warehouseId: z.string().min(1),
  mappingRevision: z.string().nullable(),
  counters: countersSchema,
  evidenceCoverage: evidenceCoverageSchema,
});
const paginationSchema = z.object({
  page: z.number().int().min(1).max(100000),
  pageSize: z.number().int().min(1).max(100),
  totalItems: count,
  totalPages: count,
  evaluatedAt: timestamp,
});
export const executionsSchema = z.object({
  data: z.object({ items: z.array(executionSchema).max(100) }),
  meta: paginationSchema,
});
export const executionItemSchema = z.object({
  executionId: z.uuid(),
  inventoryItemId: z.string().min(1),
  variantId: z.string().nullable(),
  sourceProductId: z.string().nullable(),
  compareQuantity: quantity.nullable(),
  desiredQuantity: quantity.nullable(),
  outcome: executionOutcomeSchema.exclude(['all']),
  recordedStatus: z.string().min(1),
  reasonCode: z.string().nullable(),
  dispatchedAt: timestamp.nullable(),
  completedAt: timestamp.nullable(),
  evidenceReference: z.string().nullable(),
  attributionOrigin: z.enum(['intent', 'result', 'snapshot']),
  currentSku: z.string().nullable(),
  currentProductTitle: z.string().nullable(),
  labelOrigin: z.literal('current_catalog').nullable(),
  labelsAsOf: timestamp.nullable(),
  labelAvailability: z.enum(['available', 'not_found', 'unavailable', 'not_requested']),
});
export const executionItemsSchema = z.object({
  data: z.object({ items: z.array(executionItemSchema).max(100) }),
  meta: paginationSchema.extend({
    counters: countersSchema,
    evidenceCoverage: evidenceCoverageSchema,
  }),
});
const pageInputSchema = z.strictObject({
  page: z.number().int().min(1).max(100000),
  pageSize: z.number().int().min(1).max(100),
});
const executionsInputSchema = pageInputSchema.extend({
  locationId: z
    .string()
    .regex(/^gid:\/\/shopify\/Location\/[1-9][0-9]*$/u)
    .optional(),
  status: executionStatusSchema.optional(),
  requestId: z.uuid().optional(),
});
export type StockExecution = z.infer<typeof executionSchema>;
export type StockExecutionItem = z.infer<typeof executionItemSchema>;
export type ExecutionOutcome = z.infer<typeof executionOutcomeSchema>;
export type ExecutionPage = z.infer<typeof executionsSchema>;
export type ExecutionItemsPage = z.infer<typeof executionItemsSchema>;
export type ExecutionsInput = z.infer<typeof executionsInputSchema>;

export async function getStockExecutions(
  input: ExecutionsInput,
  signal: AbortSignal
): Promise<ExecutionPage> {
  return executionsSchema.parse(
    await api.get('/api/v1/shopify/stock-sync/executions', {
      params: executionsInputSchema.parse(input),
      signal,
    })
  );
}

export async function getStockExecution(
  executionId: string,
  signal: AbortSignal
): Promise<StockExecution> {
  const id = z.uuid().parse(executionId);
  const response = z
    .object({ data: executionSchema })
    .parse(await api.get(`/api/v1/shopify/stock-sync/executions/${id}`, { signal })).data;
  if (response.executionId !== id) throw new Error('Invalid execution identity');
  return response;
}

export async function getStockExecutionItems(
  executionId: string,
  input: {
    page: number;
    pageSize: number;
    outcome: ExecutionOutcome;
    includeCurrentLabels: boolean;
  },
  signal: AbortSignal
): Promise<ExecutionItemsPage> {
  const id = z.uuid().parse(executionId);
  const params = pageInputSchema
    .extend({
      outcome: executionOutcomeSchema,
      includeCurrentLabels: z.boolean(),
    })
    .parse(input);
  const response = executionItemsSchema.parse(
    await api.get(`/api/v1/shopify/stock-sync/executions/${id}/items`, { params, signal })
  );
  if (response.data.items.some((item) => item.executionId !== id))
    throw new Error('Invalid execution identity');
  return response;
}
